import { useState, useCallback } from "react";
import FinancialRecordService from "../../../../services/financial_record_service";
import AccountingRecordService from "../../../../services/accounting_record_service";
import { FINANCIAL_STATUS, FINANCIAL_TYPES, FINANCIAL_SOURCES, FINANCIAL_PAYMENT_METHODS } from "../../../../constants/financial";
import { writeBatch } from "firebase/firestore";
import { db } from "../../../../services/firebase";

const today = () => new Date().toISOString().split("T")[0];

export const usePayablesMutations = (idTenant, authUser, refresh) => {
    const [processing, setProcessing] = useState(false);

    /**
     * Criar despesa manual (Dual Write em LIABILITY e EXPENSE) com Atomicidade.
     */
    const createPayable = useCallback(async (formData, onSuccess) => {
        if (!idTenant) return false;
        setProcessing(true);

        try {
            const batch = writeBatch(db);

            // 1. Cria a promessa de pagamento em Contas a Pagar (Caixa - LIABILITY)
            await FinancialRecordService.save({
                type: FINANCIAL_TYPES.LIABILITY,
                sourceType: FINANCIAL_SOURCES.SUPPLIER,
                idEntity: null,
                entityName: formData.recipient || "",
                idCategory: formData.idCategory || null,
                categoryName: formData.categoryName || "",
                idBankAccount: formData.idBankAccount || null,
                bankAccountName: formData.bankAccountName || "",
                description: formData.description || "",
                grossAmount: Number(formData.amount) || 0,
                feeAmount: 0,
                feePercentage: 0,
                discount: 0,
                fineAndInterest: 0,
                netAmount: Number(formData.amount) || 0,
                issueDate: today(),
                competenceDate: formData.competenceDate || formData.dueDate,
                dueDate: formData.dueDate,
                paymentDate: null,
                currentInstallment: 1,
                totalInstallments: 1,
                status: FINANCIAL_STATUS.PENDING,
                paymentMethod: FINANCIAL_PAYMENT_METHODS.OTHER,
                observation: formData.observation || "",
            }, idTenant, authUser, [], batch);

            // 2. Cria o Fato Gerador na DRE (Competência - EXPENSE)
            await AccountingRecordService.save({
                type: FINANCIAL_TYPES.EXPENSE,
                amount: Number(formData.amount) || 0,
                issueDate: today(),
                competenceDate: formData.competenceDate || formData.dueDate,
                description: formData.description || "Despesa Manual",
                idCategory: formData.idCategory || null,
                categoryName: formData.categoryName || "",
                sourceType: FINANCIAL_SOURCES.SUPPLIER,
                idSource: formData.recipient || null,
                status: "COMPLETED",
                tags: ["web_app_manual_payable"]
            }, idTenant, authUser, batch);

            await batch.commit();

            refresh();
            if (onSuccess) onSuccess();
            return true;
        } catch (err) {
            console.error("[usePayablesMutations] Erro ao criar despesa:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    /**
     * Dar baixa (marcar como PAID).
     */
    const markAsPaid = useCallback(async (recordId, paymentData = {}) => {
        if (!idTenant || !recordId) return false;
        setProcessing(true);

        try {
            await FinancialRecordService.markAsPaid(recordId, idTenant, authUser, {
                paymentDate: paymentData.paymentDate || today(),
                idBankAccount: paymentData.idBankAccount || null,
                bankAccountName: paymentData.bankAccountName || "",
                paymentMethod: paymentData.paymentMethod || null,
                amountPaid: paymentData.amountPaid ? Number(paymentData.amountPaid) : null,
                observation: paymentData.observation || "",
                documentNumber: paymentData.documentNumber || "",
            });
            refresh();
            return true;
        } catch (err) {
            console.error("[usePayablesMutations] Erro ao dar baixa:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    /**
     * Cancelar uma despesa.
     */
    const cancelRecord = useCallback(async (recordId, reason = "") => {
        if (!idTenant || !recordId) return false;
        setProcessing(true);

        try {
            await FinancialRecordService.cancel(recordId, idTenant, authUser, reason);
            refresh();
            return true;
        } catch (err) {
            console.error("[usePayablesMutations] Erro ao cancelar:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    /**
     * Criar lote de despesas importadas via IA.
     */
    const createPayablesBatch = useCallback(async (resultsArray, onSuccess) => {
        if (!idTenant || !Array.isArray(resultsArray) || resultsArray.length === 0) return false;
        setProcessing(true);

        try {
            const batch = writeBatch(db);

            for (const item of resultsArray) {
                const amount = parseFloat(item.amount) || 0;
                
                await FinancialRecordService.save({
                    type: FINANCIAL_TYPES.LIABILITY,
                    sourceType: FINANCIAL_SOURCES.SUPPLIER,
                    idEntity: null,
                    entityName: item.description || "",
                    idCategory: item.categoryId || null,
                    categoryName: item.categoryName || "",
                    idBankAccount: null,
                    bankAccountName: "",
                    description: item.description || "",
                    grossAmount: amount,
                    feeAmount: 0,
                    feePercentage: 0,
                    discount: 0,
                    fineAndInterest: 0,
                    netAmount: amount,
                    issueDate: today(),
                    competenceDate: item.dueDate,
                    dueDate: item.dueDate,
                    paymentDate: item.status === "PAID" ? item.dueDate : null,
                    currentInstallment: 1,
                    totalInstallments: 1,
                    status: item.status === "PAID" ? FINANCIAL_STATUS.PAID : FINANCIAL_STATUS.PENDING,
                    paymentMethod: item.paymentMethod || FINANCIAL_PAYMENT_METHODS.OTHER,
                    notes: "Importado via IA",
                }, idTenant, authUser, [], batch);

                await AccountingRecordService.save({
                    type: FINANCIAL_TYPES.EXPENSE,
                    amount: amount,
                    issueDate: today(),
                    competenceDate: item.dueDate,
                    description: item.description || "Despesa Importada",
                    idCategory: item.categoryId || null,
                    categoryName: item.categoryName || "",
                    sourceType: FINANCIAL_SOURCES.SUPPLIER,
                    idSource: item.description || null,
                    status: "COMPLETED",
                    tags: ["web_app_ai_payable"]
                }, idTenant, authUser, batch);
            }

            await batch.commit();

            refresh();
            if (onSuccess) onSuccess();
            return true;
        } catch (err) {
            console.error("[usePayablesMutations] Erro ao criar lote de despesas IA:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    return { processing, createPayable, createPayablesBatch, markAsPaid, cancelRecord };
};
